import { useCallback } from 'react';
import { useGetBoardCommand } from '@atlassian/jira-command-palette-common/src/common/ui/command/board/index.tsx';
import type { Command } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { BOARD_LIMIT } from '../constants.tsx';
import { messages } from '../messages.tsx';
import type { GetQuickSearchChildResultProps } from '../types.tsx';
import { getQuickSearchBoardScope, transformQuickSearchBoardResultToBoard } from '../utils.tsx';
import { useGetViewAllCommand } from '../view-all/index.tsx';
import { ViewAllType, getViewAllUrl } from '../view-all/utils.tsx';
import type {
	UseGetQuickSearchBoardCommandsProps,
	UseGetQuickSearchBoardCommandsResponse,
} from './types.tsx';

export const useGetQuickSearchBoardCommands = ({
	section,
	showViewAll,
}: UseGetQuickSearchBoardCommandsProps): UseGetQuickSearchBoardCommandsResponse => {
	const { formatMessage } = useIntl();

	const { getBoardCommand } = useGetBoardCommand();
	const { getViewAllCommand } = useGetViewAllCommand();

	const getQuickSearchBoardCommands = useCallback(
		({ search, quickSearchResponseData, error }: GetQuickSearchChildResultProps) => {
			const boardScope = getQuickSearchBoardScope(quickSearchResponseData);

			// No results
			if (!boardScope || error || boardScope?.error || boardScope?.results?.length === 0) {
				return [];
			}

			const commands: Command[] = boardScope.results.slice(0, BOARD_LIMIT).map((board) =>
				getBoardCommand({
					board: transformQuickSearchBoardResultToBoard(board),
					keywords: search,
					section,
					analytics: {
						isSearchResult: true,
					},
				}),
			);

			if (!showViewAll || boardScope.results.length <= BOARD_LIMIT) {
				return commands;
			}

			const viewAllCommand = getViewAllCommand({
				id: 'view-all-boards',
				keywords: search,
				section,
				name: formatMessage(messages.viewAllResults),
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				url: getViewAllUrl(ViewAllType.BOARD, search)!,
				analyticsPayload: { action: 'viewAllBoards', size: boardScope.size },
			});

			return commands.concat([viewAllCommand]);
		},
		[formatMessage, getBoardCommand, getViewAllCommand, section, showViewAll],
	);

	return { getQuickSearchBoardCommands };
};
